import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money, formatDateAT } from "@/lib/format";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend,
} from "recharts";
import useSmartFilters from "@/hooks/useSmartFilters";
import FilterChipBar from "@/components/FilterChipBar";
import { Plus, Trash2, Check, Upload, Receipt, FileText, Wallet, TrendingUp, TrendingDown, Landmark, RotateCw, CheckCircle2, Sparkles, X, Filter, Pencil } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import PeriodSelector, { ClosedPeriodBanner } from "@/components/PeriodSelector";
import { usePeriod } from "@/context/PeriodContext";
import DataTable from "@/components/DataTable";
import EmptyState from "@/components/EmptyState";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton";
import { SkeletonKPICard } from "@/components/Skeleton";
import RevenueImportWizard from "@/components/RevenueImportWizard";
import OcrExpenseDialog from "@/components/OcrExpenseDialog";
import BankImportTab from "@/components/BankImportTab";
import { useEffect } from "react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Finance() {
  const { t, lang, localized } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (v) => { const p = new URLSearchParams(params); p.set("tab", v); setParams(p); };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        title={t("finance")}
        description={lang === "tr" ? "Ciro · Gider · Fatura · Nakit akışı — tek panelde" : "Umsatz · Ausgaben · Rechnungen · Cashflow — an einem Ort"}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto p-1 bg-muted/50 rounded-xl">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-lg">{t("overview")}</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue" className="rounded-lg">{t("revenue")}</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses" className="rounded-lg">{t("expenses")}</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices" className="rounded-lg">{t("invoices")}</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers" className="rounded-lg">{t("suppliers")}</TabsTrigger>
          <TabsTrigger value="cashflow" data-testid="tab-cashflow" className="rounded-lg">{t("cashFlow")}</TabsTrigger>
          <TabsTrigger value="taxes" data-testid="tab-taxes" className="rounded-lg">{t("taxes")}</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="tab-pnl" className="rounded-lg">{t("profitLoss")}</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank" className="rounded-lg">{t("bankAccounts")}</TabsTrigger>
          <TabsTrigger value="bank-import" data-testid="tab-bank-import" className="rounded-lg">{lang === "tr" ? "Banka İçe Aktar" : "Bankimport"}</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="revenue"><RevenueTab /></TabsContent>
          <TabsContent value="expenses"><ExpensesTab localized={localized} /></TabsContent>
          <TabsContent value="invoices"><InvoicesTab initialType={params.get("type")} /></TabsContent>
          <TabsContent value="suppliers"><SuppliersTab /></TabsContent>
          <TabsContent value="cashflow"><CashFlowTab /></TabsContent>
          <TabsContent value="taxes"><TaxesTab /></TabsContent>
          <TabsContent value="pnl"><PnLTab /></TabsContent>
          <TabsContent value="bank"><BankAccountsTab /></TabsContent>
          <TabsContent value="bank-import"><BankImportTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

/* ===== Overview ===== */
function Overview() {
  const { t, lang } = useI18n();
  const { periodKey, isSelectedClosed, currentPeriodKey } = usePeriod();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => { (async () => setData((await api.get("/finance/dashboard")).data))(); }, []);
  useEffect(() => {
    if (!periodKey) return;
    (async () => {
      const { data } = await api.get(`/finance/summary?period=${periodKey}`);
      setSummary(data);
    })();
  }, [periodKey]);
  if (!data) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[0,1,2,3].map((i) => <SkeletonKPICard key={i} />)}</div>;
  const isCurrent = periodKey === currentPeriodKey;
  return (
    <div className="space-y-4">
      {summary && (
        <Card className="border rounded-xl shadow-elev-1" data-testid="period-summary-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-display font-semibold tracking-tight">
              {(lang === "tr" ? "Ay Özeti — " : "Monatszusammenfassung — ") + periodKey}
              {isSelectedClosed && (
                <Badge variant="outline" className="ml-2 border-slate-300 text-slate-600">{lang === "tr" ? "Kapalı Ay" : "Geschlossen"}</Badge>
              )}
              {isCurrent && !isSelectedClosed && (
                <Badge className="ml-2 bg-emerald-600 hover:bg-emerald-600">{lang === "tr" ? "Canlı Ay" : "Aktueller Monat"}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={t("revenue")} value={money(summary.total_revenue)} icon={TrendingUp} />
              <Stat label={t("expenses")} value={money(summary.total_expenses)} icon={TrendingDown} />
              <Stat label={lang === "tr" ? "Kâr" : "Gewinn"} value={money(summary.total_profit)} accent icon={Wallet} />
              <Stat label={lang === "tr" ? "Net KDV" : "Netto-USt."} value={money(summary.vat_summary?.net_vat || 0)} icon={Receipt} />
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Stat label={t("revenueMtd")} value={money(data.revenue_mtd)} icon={TrendingUp} />
        <Stat label={t("expensesMtd")} value={money(data.expenses_mtd)} icon={TrendingDown} />
        <Stat label={t("profitMtd")} value={money(data.profit_mtd)} accent icon={Wallet} />
        <Stat label={t("cashOnHand")} value={money(data.cash_on_hand)} icon={Landmark} />
      </div>
      <Card className="border rounded-xl shadow-elev-1">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display font-semibold tracking-tight">{t("last6Months")}</CardTitle>
        </CardHeader>
        <CardContent className="h-72 pt-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.chart_6m} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="month" fontSize={11} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false} />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v}`} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money(v)} cursor={{ fill: "hsl(var(--muted) / 0.3)" }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
              <Bar dataKey="revenue" name={t("revenue")} fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={40} />
              <Bar dataKey="expenses" name={t("expenses")} fill="hsl(0 84% 60%)" radius={[6, 6, 0, 0]} maxBarSize={40} />
              <Bar dataKey="profit" name={lang === "tr" ? "Kâr" : "Gewinn"} fill="hsl(158 64% 42%)" radius={[6, 6, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

/* ===== Revenue ===== */
function RevenueTab() {
  const { t, lang } = useI18n();
  const { periodKey, isSelectedClosed } = usePeriod();
  const [open, setOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const empty = { date: todayISO(), period: "daily", cash: 0, card: 0, online: 0, bank_transfer: 0, other: 0, notes: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.date) e.date = lang === "tr" ? "Tarih zorunlu" : "Datum erforderlich";
    if (form.cash < 0 || form.card < 0 || form.online < 0) e.amount = "≥ 0";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const submit = async () => {
    if (!validate()) return;
    try {
      await api.post("/revenue", { ...form, cash: +form.cash, card: +form.card, online: +form.online, bank_transfer: +form.bank_transfer, other: +form.other });
      setOpen(false); setForm(empty); setErrors({}); setRefreshKey((k) => k + 1);
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    await api.delete(`/revenue/${id}`);
    setRefreshKey((k) => k + 1);
    toast.success(t("delete"));
  };

  const columns = [
    { key: "date", label: t("date"), sortable: true, className: "font-mono text-sm", render: (r) => formatDateAT(r.date) },
    { key: "period", label: lang === "tr" ? "Dönem" : "Zeitraum", render: (r) => <Badge variant="secondary" className="font-normal">{r.period === "daily" ? t("revenueDaily") : t("revenueMonthly")}</Badge> },
    { key: "cash", label: t("cash"), className: "text-right font-mono", render: (r) => money(r.cash) },
    { key: "card", label: t("card"), className: "text-right font-mono", render: (r) => money(r.card) },
    { key: "online", label: t("online"), className: "text-right font-mono", render: (r) => money(r.online) },
    { key: "total", label: t("total"), sortable: true, className: "text-right font-mono font-semibold", render: (r) => money(r.total) },
    { key: "actions", label: "", className: "text-right w-10", render: (r) => (
      <ConfirmDeleteButton onConfirm={() => remove(r.id)} itemName={r.description || `${money(r.amount)}`} data-testid={`del-rev-${r.id}`} />
    )},
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => setCsvOpen(true)} data-testid="import-csv-btn" disabled={isSelectedClosed}><Upload className="w-4 h-4 mr-2" /> {t("revenueCsv")}</Button>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="new-revenue-btn" disabled={isSelectedClosed}><Plus className="w-4 h-4 mr-2" /> {t("newRevenue")}</Button>
      </div>

      <DataTable
        columns={columns}
        fetcher={async (p) => {
          const params = new URLSearchParams({ page: p.page, page_size: p.page_size, sort: p.sort || "date", order: p.order || "desc" });
          if (periodKey) params.set("period_key", periodKey);
          const { data } = await api.get(`/revenue?${params.toString()}`);
          return data;
        }}
        defaultSort="date"
        pageSize={10}
        refreshKey={`${refreshKey}-${periodKey || ""}`}
        rowTestId="rev-row"
        emptyProps={{ icon: TrendingUp, title: lang === "tr" ? "Henüz ciro girişi yok" : "Noch keine Umsätze", description: lang === "tr" ? "İlk günün cirosunu ekle." : "Erfasse deinen ersten Umsatz.", action: { label: t("newRevenue"), icon: Plus, onClick: () => { setForm(empty); setOpen(true); }, testid: "empty-new-rev" } }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("newRevenue")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="rev-date" className="mt-1" />{errors.date && <p className="text-xs text-destructive mt-1">{errors.date}</p>}</div>
              <div>
                <Label>{lang === "tr" ? "Dönem" : "Zeitraum"}</Label>
                <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                  <SelectTrigger data-testid="rev-period" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="daily">{t("revenueDaily")}</SelectItem><SelectItem value="monthly">{t("revenueMonthly")}</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {[["cash", t("cash")], ["card", t("card")], ["online", t("online")], ["bank_transfer", t("bankTransfer")], ["other", t("other")]].map(([k, l]) => (
              <div key={k} className="grid grid-cols-3 items-center gap-3">
                <Label className="text-sm">{l}</Label>
                <Input className="col-span-2 font-mono" type="number" step="0.01" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`rev-${k}`} />
              </div>
            ))}
            {errors.amount && <p className="text-xs text-destructive">{errors.amount}</p>}
            <div className="text-sm pt-1 flex justify-between font-mono border-t">
              <span className="text-muted-foreground">{t("total")}</span>
              <span className="font-semibold">{money((+form.cash) + (+form.card) + (+form.online) + (+form.bank_transfer) + (+form.other))}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-rev">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog open={csvOpen} onOpenChange={setCsvOpen} onDone={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
}

// CsvImportDialog is now the RevenueImportWizard component
function CsvImportDialog(props) {
  return <RevenueImportWizard {...props} />;
}

/* ===== Expenses ===== */
function ExpensesTab({ localized }) {
  const { t, lang } = useI18n();
  const { periodKey, isSelectedClosed } = usePeriod();
  const { get, set, clear } = useSmartFilters("expenses", {}, { protectedKeys: ["tab"] });
  const [cats, setCats] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const catFilter = get("category_id") || "all";
  const supplierFilter = get("supplier_id") || "";
  const period = get("period") || "";
  const setCatFilter = (v) => set("category_id", v === "all" ? null : v);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  // Sprint 29.2 — edit flow. `editingId` null = create new; set = edit existing.
  const [editingId, setEditingId] = useState(null);
  const empty = {
    date: todayISO(), amount: 0, category_id: "", supplier_id: "", bank_account_id: "",
    vendor: "", description: "", tax_amount: 0, is_recurring: false, recurring_period: "monthly",
    payment_status: "paid",
  };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [ocrOpen, setOcrOpen] = useState(false);

  useEffect(() => { (async () => {
    const [c, a, s] = await Promise.all([
      api.get("/expense-categories"),
      api.get("/bank-accounts"),
      api.get("/suppliers?page_size=100"),
    ]);
    setCats(c.data); setAccounts(a.data); setSuppliers(s.data.items || []);
  })(); }, [refreshKey]);

  const validate = () => {
    const e = {};
    if (!form.date) e.date = lang === "tr" ? "Tarih zorunlu" : "Datum erforderlich";
    if (!(+form.amount > 0)) e.amount = lang === "tr" ? "Tutar > 0 olmalı" : "Betrag > 0";
    if (!form.vendor?.trim()) e.vendor = lang === "tr" ? "Tedarikçi zorunlu" : "Lieferant erforderlich";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const openCreate = () => {
    setEditingId(null); setForm(empty); setErrors({}); setOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setErrors({});
    setForm({
      date: row.date || todayISO(),
      amount: row.amount ?? 0,
      category_id: row.category_id || "",
      supplier_id: row.supplier_id || "",
      bank_account_id: row.bank_account_id || "",
      vendor: row.vendor || "",
      description: row.description || "",
      tax_amount: row.tax_amount ?? 0,
      is_recurring: !!row.is_recurring,
      recurring_period: row.recurring_period || "monthly",
      payment_status: row.payment_status || "paid",
      line_items: Array.isArray(row.line_items) ? row.line_items : [],
      source: row.source || "manual",
    });
    setOpen(true);
  };
  const submit = async () => {
    if (!validate()) return;
    const payload = {
      ...form, amount: +form.amount, tax_amount: +form.tax_amount,
      category_id: form.category_id || null,
      supplier_id: form.supplier_id || null,
      bank_account_id: form.bank_account_id || null,
      recurring_period: form.is_recurring ? form.recurring_period : "none",
    };
    try {
      if (editingId) {
        await api.put(`/expenses/${editingId}`, payload);
        toast.success(lang === "tr" ? "Gider güncellendi" : "Ausgabe aktualisiert");
      } else {
        await api.post("/expenses", payload);
        toast.success(t("save"));
      }
      setOpen(false); setForm(empty); setEditingId(null); setErrors({}); setRefreshKey((k) => k + 1);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "period_closed") {
        toast.error(lang === "tr"
          ? `Bu ay kapalı (${det.period_key}) — düzenleme yapılamaz.`
          : `Monat geschlossen (${det.period_key}) — Bearbeitung nicht möglich.`);
      } else {
        toast.error(formatApiErrorDetail(det));
      }
    }
  };
  const remove = async (id) => { await api.delete(`/expenses/${id}`); setRefreshKey((k) => k + 1); toast.success(t("delete")); };

  const columns = [
    { key: "date", label: t("date"), sortable: true, className: "font-mono text-sm w-28", render: (r) => formatDateAT(r.date) },
    { key: "vendor", label: t("vendor"), sortable: true, className: "font-medium", render: (r) => (
      <div className="flex items-center gap-2">
        <span>{r.vendor}</span>
        {r.is_recurring && <RotateCw className="w-3 h-3 text-muted-foreground" title="recurring" />}
      </div>
    )},
    { key: "category", label: t("category"), render: (r) => {
      const c = cats.find((x) => x.id === r.category_id);
      return c ? <span className="inline-flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-sm" style={{ background: c.color }} />{localized(c.name)}</span> : <span className="text-xs text-muted-foreground">—</span>;
    }},
    { key: "amount", label: t("amount"), sortable: true, className: "text-right font-mono", render: (r) => money(r.amount) },
    { key: "tax_amount", label: t("taxAmount"), className: "text-right font-mono text-xs text-muted-foreground", render: (r) => money(r.tax_amount) },
    { key: "actions", label: "", className: "text-right w-20", render: (r) => (
      <div className="flex items-center justify-end gap-1"
           onClick={(e) => e.stopPropagation()}
           onMouseDown={(e) => e.stopPropagation()}>
        <Button
          size="icon" variant="ghost"
          onClick={() => openEdit(r)}
          className="h-8 w-8 rounded-lg hover:bg-primary/10"
          title={lang === "tr" ? "Düzenle" : "Bearbeiten"}
          data-testid={`edit-exp-${r.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <ConfirmDeleteButton onConfirm={() => remove(r.id)} itemName={r.description || r.supplier_name || money(r.amount)} data-testid={`del-exp-${r.id}`} />
      </div>
    )},
  ];

  const toolbar = (
    <Select value={catFilter} onValueChange={(v) => setCatFilter(v)}>
      <SelectTrigger className="w-full sm:w-52 h-9" data-testid="exp-cat-filter"><SelectValue placeholder={t("category")} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{lang === "tr" ? "Tüm kategoriler" : "Alle Kategorien"}</SelectItem>
        {cats.map((c) => <SelectItem key={c.id} value={c.id}>{localized(c.name)}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const activeCatName = catFilter && catFilter !== "all" ? localized((cats.find((c) => c.id === catFilter) || {}).name) : null;
  const activeSupName = supplierFilter ? (suppliers.find((s) => s.id === supplierFilter) || {}).name : null;
  const periodLabel = { this_month: (lang === "tr" ? "Bu ay" : "Diesen Monat"), last_30_days: (lang === "tr" ? "Son 30 gün" : "Letzte 30 Tage"), this_year: (lang === "tr" ? "Bu yıl" : "Dieses Jahr") }[period];
  const activeChips = [
    activeCatName && { key: "category_id", label: `${t("category")}: ${activeCatName}`, onClear: () => set("category_id", null) },
    activeSupName && { key: "supplier_id", label: `${lang === "tr" ? "Tedarikçi" : "Lieferant"}: ${activeSupName}`, onClear: () => set("supplier_id", null) },
    periodLabel && { key: "period", label: periodLabel, onClear: () => set("period", null) },
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => setOcrOpen(true)} data-testid="ocr-expense-btn" className="h-10 rounded-lg" disabled={isSelectedClosed}>
          <Sparkles className="w-4 h-4 mr-2 text-primary" /> {lang === "tr" ? "OCR ile Yükle" : "OCR-Import"}
        </Button>
        <Button onClick={openCreate} data-testid="new-expense-btn" className="h-10 rounded-lg shadow-elev-2" disabled={isSelectedClosed}><Plus className="w-4 h-4 mr-2" /> {t("newExpense")}</Button>
      </div>
      {activeChips.length > 0 && <FilterChipBar chips={activeChips} onClearAll={clear} />}
      <DataTable
        columns={columns}
        toolbar={toolbar}
        onRowClick={(row) => openEdit(row)}
        fetcher={async (p) => {
          const params = new URLSearchParams({ page: p.page, page_size: p.page_size, sort: p.sort || "date", order: p.order || "desc" });
          if (p.search) params.set("search", p.search);
          if (catFilter && catFilter !== "all") params.set("category_id", catFilter);
          if (supplierFilter) params.set("supplier_id", supplierFilter);
          if (period) params.set("period", period);
          if (periodKey) params.set("period_key", periodKey);
          const { data } = await api.get(`/expenses?${params.toString()}`);
          return data;
        }}
        defaultSort="date"
        pageSize={10}
        refreshKey={`${refreshKey}-${catFilter}-${supplierFilter}-${period}-${periodKey || ""}`}
        rowTestId="exp-row"
        searchPlaceholder={lang === "tr" ? "Tedarikçi / açıklama ara…" : "Suchen…"}
        emptyProps={{ icon: Receipt, title: lang === "tr" ? "Gider yok" : "Keine Ausgaben", description: lang === "tr" ? "İlk gideri kaydet — kategorilere ayır." : "Erfasse deine erste Ausgabe.", action: { label: t("newExpense"), icon: Plus, onClick: openCreate, testid: "empty-new-exp" } }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="!max-w-md !w-full
                     !left-0 !top-auto !bottom-0 !right-auto !translate-x-0 !translate-y-0
                     !h-[100dvh] !max-h-[100dvh]
                     sm:!left-[50%] sm:!top-[50%] sm:!bottom-auto sm:!right-auto
                     sm:!translate-x-[-50%] sm:!translate-y-[-50%]
                     sm:!h-auto sm:!max-h-[92vh] sm:!w-[calc(100vw-2rem)]
                     !p-0 !gap-0 flex flex-col rounded-none sm:rounded-lg overflow-hidden"
          data-testid="expense-dialog"
        >
          <DialogHeader className="flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4 border-b bg-background">
            <DialogTitle data-testid="expense-dialog-title">
              {editingId
                ? (lang === "tr" ? "Gideri Düzenle" : "Ausgabe bearbeiten")
                : t("newExpense")}
            </DialogTitle>
          </DialogHeader>
          <div
            className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 space-y-3"
            style={{ WebkitOverflowScrolling: "touch" }}
            data-testid="expense-dialog-body"
          >
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" className="mt-1" />{errors.date && <p className="text-xs text-destructive mt-1">{errors.date}</p>}</div>
              <div><Label>{t("amount")} (€)</Label><Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" className="mt-1 font-mono" />{errors.amount && <p className="text-xs text-destructive mt-1">{errors.amount}</p>}</div>
            </div>
            <div><Label>{t("vendor")}</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="exp-vendor" className="mt-1" />{errors.vendor && <p className="text-xs text-destructive mt-1">{errors.vendor}</p>}</div>
            <div>
              <Label>{t("category")}</Label>
              <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="exp-cat" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{localized(c.name)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{lang === "tr" ? "Tedarikçi" : "Lieferant"}</Label>
              <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v, vendor: v !== "none" ? (suppliers.find((s) => s.id === v)?.name || form.vendor) : form.vendor })}>
                <SelectTrigger data-testid="exp-supplier" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("paymentStatus")}</Label>
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger data-testid="exp-payment" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">{t("paid")}</SelectItem>
                  <SelectItem value="partial">{t("partial")}</SelectItem>
                  <SelectItem value="unpaid">{t("unpaid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("bankAccount")}</Label>
              <Select value={form.bank_account_id || "none"} onValueChange={(v) => setForm({ ...form, bank_account_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("taxAmount")} (€)</Label><Input type="number" step="0.01" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} className="mt-1 font-mono" /></div>
            <div><Label>{t("description")}</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label className="text-sm">{lang === "tr" ? "Tekrarlı gider" : "Wiederkehrend"}</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">{lang === "tr" ? "Kira, faturalar vs." : "z. B. Miete, Nebenkosten"}</p>
              </div>
              <Switch checked={form.is_recurring} onCheckedChange={(v) => setForm({ ...form, is_recurring: v })} data-testid="exp-recurring" />
            </div>
            {form.is_recurring && (
              <Select value={form.recurring_period} onValueChange={(v) => setForm({ ...form, recurring_period: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">{lang === "tr" ? "Haftalık" : "Wöchentlich"}</SelectItem>
                  <SelectItem value="monthly">{lang === "tr" ? "Aylık" : "Monatlich"}</SelectItem>
                  <SelectItem value="yearly">{lang === "tr" ? "Yıllık" : "Jährlich"}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div
            className="flex-shrink-0 flex justify-end gap-2 px-4 py-3 sm:px-6 border-t bg-background/95 backdrop-blur
                       [padding-bottom:calc(env(safe-area-inset-bottom)+0.75rem)]"
            data-testid="expense-dialog-footer"
          >
            <Button variant="outline" onClick={() => setOpen(false)} className="min-h-[44px]" data-testid="cancel-exp">
              {t("cancel")}
            </Button>
            <Button onClick={submit} className="min-h-[44px]" data-testid="save-exp">
              {editingId
                ? (lang === "tr" ? "Değişiklikleri Kaydet" : "Änderungen speichern")
                : t("save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <OcrExpenseDialog
        open={ocrOpen}
        onOpenChange={setOcrOpen}
        onSaved={() => setRefreshKey((k) => k + 1)}
        suppliers={suppliers}
        categories={cats}
        accounts={accounts}
        localized={localized}
      />
    </div>
  );
}

/* ===== Invoices ===== */
function InvoicesTab({ initialType }) {
  const { t, lang } = useI18n();
  const { periodKey, isSelectedClosed } = usePeriod();
  const { get, set, clear } = useSmartFilters("invoices", {}, { protectedKeys: ["tab", "type"] });
  const [type, setTypeState] = useState(initialType === "ar" ? "ar" : "ap");
  const setType = (v) => { setTypeState(v); set("type", v === "ap" ? null : v); };
  const status = get("status");
  const isOverdue = get("filter") === "overdue";
  const dueWithin = get("due_within_days");
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const empty = { type, number: "", party_name: "", issue_date: todayISO(), due_date: "", amount_net: 0, tax_rate: 20, status: "open", notes: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const hasFilter = isOverdue || !!status || !!dueWithin;

  useEffect(() => { setForm((f) => ({ ...f, type })); }, [type]);

  const validate = () => {
    const e = {};
    if (!form.party_name?.trim()) e.party_name = lang === "tr" ? "Taraf zorunlu" : "Partei erforderlich";
    if (!(+form.amount_net > 0)) e.amount = lang === "tr" ? "Net > 0" : "Netto > 0";
    if (!form.issue_date) e.issue_date = "req";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const submit = async () => {
    if (!validate()) return;
    try {
      await api.post("/invoices", { ...form, amount_net: +form.amount_net, tax_rate: +form.tax_rate, amount_tax: 0, amount_total: 0, due_date: form.due_date || null });
      setOpen(false); setForm({ ...empty, type }); setErrors({}); setRefreshKey((k) => k + 1);
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const markPaid = async (id) => { await api.post(`/invoices/${id}/mark-paid`); setRefreshKey((k) => k + 1); toast.success(t("invoiceStatusPaid")); };
  const remove = async (id) => { await api.delete(`/invoices/${id}`); setRefreshKey((k) => k + 1); toast.success(t("delete")); };

  const statusBadge = (s) => {
    const map = {
      draft:   { className: "bg-muted text-muted-foreground border-transparent", label: t("invoiceStatusDraft") },
      open:    { className: "bg-blue-50 text-blue-700 border-blue-200", label: t("invoiceStatusOpen") },
      paid:    { className: "bg-emerald-50 text-emerald-700 border-emerald-200", label: t("invoiceStatusPaid") },
      overdue: { className: "bg-red-50 text-red-700 border-red-200", label: t("invoiceStatusOverdue") },
    };
    const c = map[s] || map.open;
    return <Badge variant="outline" className={`${c.className} rounded-md font-medium`}>{c.label}</Badge>;
  };

  const columns = [
    { key: "number", label: t("invoiceNumber"), sortable: false, className: "font-mono text-sm w-32", render: (r) => r.number || "—" },
    { key: "party_name", label: t("partyName"), sortable: true, className: "font-medium" },
    { key: "issue_date", label: t("issueDate"), sortable: true, className: "font-mono text-sm w-28", render: (r) => formatDateAT(r.issue_date) },
    { key: "due_date", label: t("dueDate"), sortable: true, className: "font-mono text-sm w-28 text-muted-foreground", render: (r) => r.due_date ? formatDateAT(r.due_date) : "—" },
    { key: "amount_total", label: t("total"), sortable: true, className: "text-right font-mono font-semibold w-28", render: (r) => money(r.amount_total) },
    { key: "status", label: t("status"), sortable: true, className: "w-28", render: (r) => statusBadge(r.status) },
    { key: "actions", label: "", className: "text-right w-24", render: (r) => (
      <div className="flex justify-end gap-0.5">
        {r.status !== "paid" && (
          <Button size="icon" variant="ghost" onClick={() => markPaid(r.id)} title={t("markPaid")} data-testid={`mark-paid-${r.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
        )}
        <ConfirmDeleteButton onConfirm={() => remove(r.id)} itemName={r.number || r.party_name} data-testid={`del-inv-${r.id}`} />
      </div>
    )},
  ];

  const toolbar = (
    <div className="inline-flex bg-muted rounded-lg p-1 shadow-inner">
      <button
        onClick={() => setType("ap")}
        className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${type === "ap" ? "bg-background shadow-elev-1 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="tab-inv-ap"
      >{t("apInvoices")}</button>
      <button
        onClick={() => setType("ar")}
        className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${type === "ar" ? "bg-background shadow-elev-1 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="tab-inv-ar"
      >{t("arInvoices")}</button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setForm({ ...empty, type }); setOpen(true); }} data-testid="new-invoice-btn" disabled={isSelectedClosed}><Plus className="w-4 h-4 mr-2" /> {t("newInvoice")}</Button>
      </div>
      {hasFilter && (
        <FilterChipBar
          chips={[
            isOverdue && { key: "overdue", label: t("invoiceStatusOverdue"), onClear: () => set("filter", null) },
            status && { key: "status", label: `${t("status")}: ${status}`, onClear: () => set("status", null) },
            dueWithin && { key: "due_within_days", label: lang === "tr" ? `${dueWithin} gün içinde` : `in ${dueWithin} Tagen`, onClear: () => set("due_within_days", null) },
          ].filter(Boolean)}
          onClearAll={clear}
        />
      )}
      <DataTable
        columns={columns}
        toolbar={toolbar}
        fetcher={async (p) => {
          const params = new URLSearchParams({ type, page: p.page, page_size: p.page_size, sort: p.sort || "issue_date", order: p.order || "desc" });
          if (p.search) params.set("search", p.search);
          if (isOverdue) params.set("overdue", "true");
          if (status && !isOverdue) params.set("status", status);
          if (dueWithin && !isOverdue) params.set("due_within_days", dueWithin);
          if (periodKey) params.set("period_key", periodKey);
          const { data } = await api.get(`/invoices?${params.toString()}`);
          return data;
        }}
        defaultSort="issue_date"
        pageSize={10}
        refreshKey={`${type}-${isOverdue}-${status || ""}-${dueWithin || ""}-${refreshKey}-${periodKey || ""}`}
        rowTestId="inv-row"
        searchPlaceholder={lang === "tr" ? "Fatura no / taraf ara…" : "Suchen…"}
        emptyProps={{ icon: FileText, title: lang === "tr" ? "Fatura yok" : "Keine Rechnungen", description: type === "ap" ? (lang === "tr" ? "Tedarikçi faturalarını ekle." : "Erfasse Lieferantenrechnungen.") : (lang === "tr" ? "Müşterilerine kestiğin faturaları ekle." : "Erfasse Ausgangsrechnungen."), action: { label: t("newInvoice"), icon: Plus, onClick: () => { setForm({ ...empty, type }); setOpen(true); }, testid: "empty-new-inv" } }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("newInvoice")} · {type === "ap" ? t("apInvoices") : t("arInvoices")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("invoiceNumber")}</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1 font-mono" data-testid="inv-number" /></div>
              <div><Label>{t("partyName")}</Label><Input value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} className="mt-1" data-testid="inv-party" />{errors.party_name && <p className="text-xs text-destructive mt-1">{errors.party_name}</p>}</div>
              <div><Label>{t("issueDate")}</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("dueDate")}</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("netAmount")} (€)</Label><Input type="number" step="0.01" min="0" value={form.amount_net} onChange={(e) => setForm({ ...form, amount_net: e.target.value })} className="mt-1 font-mono" data-testid="inv-net" />{errors.amount && <p className="text-xs text-destructive mt-1">{errors.amount}</p>}</div>
              <div><Label>{t("taxRate")} (%)</Label><Input type="number" step="1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="mt-1 font-mono" /></div>
            </div>
            <div>
              <Label>{t("status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t("invoiceStatusDraft")}</SelectItem>
                  <SelectItem value="open">{t("invoiceStatusOpen")}</SelectItem>
                  <SelectItem value="paid">{t("invoiceStatusPaid")}</SelectItem>
                  <SelectItem value="overdue">{t("invoiceStatusOverdue")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("notes")}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            <div className="pt-2 text-sm border-t flex justify-between font-mono">
              <span className="text-muted-foreground">{t("total")}</span>
              <span className="font-semibold">{money((+form.amount_net) * (1 + (+form.tax_rate) / 100))}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-invoice">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===== CashFlow ===== */
function CashFlowTab() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [txRefresh, setTxRefresh] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [txOpen, setTxOpen] = useState(false);
  const empty = { date: todayISO(), bank_account_id: "", kind: "debit", amount: 0, description: "", reference: "" };
  const [form, setForm] = useState(empty);

  useEffect(() => { (async () => setData((await api.get("/finance/cashflow?days=30")).data))(); }, []);
  useEffect(() => { (async () => setAccounts((await api.get("/bank-accounts")).data))(); }, []);

  const submitTx = async () => {
    if (!form.bank_account_id) return toast.error(lang === "tr" ? "Hesap seç" : "Konto wählen");
    try {
      await api.post("/bank-transactions", { ...form, amount: +form.amount });
      setTxOpen(false); setForm(empty); setTxRefresh((k) => k + 1);
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const reconcile = async (id, reconciled) => {
    await api.put(`/bank-transactions/${id}/reconcile`, { reconciled });
    setTxRefresh((k) => k + 1);
  };
  const removeTx = async (id) => {
    try {
      await api.delete(`/bank-transactions/${id}`);
      setTxRefresh((k) => k + 1);
      toast.success(t("delete"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const txColumns = [
    { key: "date", label: t("date"), sortable: true, className: "font-mono text-sm w-28", render: (r) => formatDateAT(r.date) },
    { key: "account", label: t("bankAccount"), render: (r) => accounts.find((a) => a.id === r.bank_account_id)?.name || "—" },
    { key: "description", label: t("description") },
    { key: "reference", label: lang === "tr" ? "Referans" : "Referenz", className: "font-mono text-xs text-muted-foreground" },
    { key: "amount", label: t("amount"), sortable: true, className: "text-right font-mono", render: (r) => (
      <span className={r.kind === "credit" ? "text-emerald-600" : "text-destructive"}>
        {r.kind === "credit" ? "+" : "−"}{money(r.amount)}
      </span>
    )},
    { key: "reconciled", label: lang === "tr" ? "Muhasebe" : "Abgleich", className: "w-24", render: (r) => (
      <button onClick={() => reconcile(r.id, !r.reconciled)} data-testid={`recon-${r.id}`} className="inline-flex items-center gap-1">
        {r.reconciled ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="w-4 h-4 rounded-full border-2" />}
        <span className="text-xs text-muted-foreground">{r.reconciled ? "OK" : "?"}</span>
      </button>
    )},
    { key: "actions", label: "", className: "text-right w-10", render: (r) => (
      <ConfirmDeleteButton onConfirm={() => removeTx(r.id)} itemName={r.description || money(r.amount)} data-testid={`del-tx-${r.id}`} />
    )},
  ];

  return (
    <div className="space-y-6">
      {data ? (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat label={lang === "tr" ? "Nakit girişi" : "Zufluss"} value={money(data.total_inflow)} icon={TrendingUp} accent />
            <Stat label={lang === "tr" ? "Nakit çıkışı" : "Abfluss"} value={money(data.total_outflow)} icon={TrendingDown} />
            <Stat label="Net" value={money(data.total_inflow - data.total_outflow)} accent icon={Wallet} />
          </div>
          <Card className="border rounded-xl shadow-elev-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display font-semibold tracking-tight">{lang === "tr" ? "Nakit akışı" : "Cashflow"}</CardTitle>
              <p className="text-xs text-muted-foreground">30 {lang === "tr" ? "gün" : "Tage"}</p>
            </CardHeader>
            <CardContent className="p-4 pt-2 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.chart} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="in" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient>
                    <linearGradient id="out" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(0 84% 60%)" stopOpacity={0.30} /><stop offset="100%" stopColor="hsl(0 84% 60%)" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" vertical={false} />
                  <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))" tickFormatter={(d) => (d || "").slice(5)} axisLine={false} tickLine={false} />
                  <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${v}`} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => money(v)} labelFormatter={formatDateAT} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                  <Area type="monotone" dataKey="inflow" name="Inflow" stroke="hsl(var(--primary))" fill="url(#in)" strokeWidth={2} />
                  <Area type="monotone" dataKey="outflow" name="Outflow" stroke="hsl(0 84% 60%)" fill="url(#out)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      ) : <div className="grid grid-cols-3 gap-3">{[0,1,2].map((i) => <SkeletonKPICard key={i} />)}</div>}

      {/* Bank Transactions section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-semibold">{lang === "tr" ? "Banka Hareketleri" : "Bank-Transaktionen"}</h3>
            <p className="text-xs text-muted-foreground">{lang === "tr" ? "Manuel giriş + mutabakat" : "Manuelle Buchung + Abgleich"}</p>
          </div>
          <Button size="sm" onClick={() => { setForm(empty); setTxOpen(true); }} data-testid="new-tx-btn"><Plus className="w-4 h-4 mr-2" /> {lang === "tr" ? "Hareket ekle" : "Transaktion"}</Button>
        </div>
        <DataTable
          columns={txColumns}
          fetcher={async (p) => {
            const params = new URLSearchParams({ page: p.page, page_size: p.page_size, sort: p.sort || "date", order: p.order || "desc" });
            if (p.search) params.set("search", p.search);
            const { data } = await api.get(`/bank-transactions?${params.toString()}`);
            return data;
          }}
          defaultSort="date"
          pageSize={8}
          refreshKey={txRefresh}
          rowTestId="tx-row"
          searchPlaceholder={lang === "tr" ? "Açıklama / referans ara…" : "Suchen…"}
          emptyProps={{ icon: Landmark, title: lang === "tr" ? "Banka hareketi yok" : "Keine Transaktionen", description: lang === "tr" ? "Manuel giriş yap veya ilerde banka entegrasyonu bekle." : "Manuell buchen oder Bankintegration abwarten.", action: { label: lang === "tr" ? "Hareket ekle" : "Transaktion", icon: Plus, onClick: () => setTxOpen(true), testid: "empty-new-tx" } }}
        />
      </div>

      <Dialog open={txOpen} onOpenChange={setTxOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{lang === "tr" ? "Banka Hareketi" : "Bank-Transaktion"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="tx-date" /></div>
              <div>
                <Label>{lang === "tr" ? "Tür" : "Typ"}</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-1" data-testid="tx-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">{lang === "tr" ? "Giriş (+)" : "Eingang (+)"}</SelectItem>
                    <SelectItem value="debit">{lang === "tr" ? "Çıkış (−)" : "Ausgang (−)"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("bankAccount")}</Label>
              <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
                <SelectTrigger className="mt-1" data-testid="tx-account"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("amount")} (€)</Label><Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 font-mono" data-testid="tx-amount" /></div>
            <div><Label>{t("description")}</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="tx-desc" /></div>
            <div><Label>{lang === "tr" ? "Referans" : "Referenz"}</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 font-mono" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTxOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submitTx} data-testid="save-tx">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===== Taxes ===== */
function TaxesTab() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { (async () => setData((await api.get("/finance/tax-summary")).data))(); }, []);
  if (!data) return <div className="grid grid-cols-4 gap-3">{[0,1,2,3].map((i) => <SkeletonKPICard key={i} />)}</div>;
  return (
    <div className="space-y-4">
      <Card className="border rounded-xl shadow-elev-1">
        <CardHeader className="pb-3"><CardTitle className="text-base font-display font-semibold tracking-tight">{t("quarterlyVat")} · {data.period}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label={t("revenue")} value={money(data.revenue_gross)} />
          <Stat label={t("outputVat")} value={money(data.output_vat)} />
          <Stat label={t("inputVat")} value={money(data.input_vat)} />
          <Stat label={t("netVat")} value={money(data.net_vat_payable)} accent />
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">
        {lang === "tr" ? "Bilgi amaçlıdır; resmi UStVA/BMD raporu Faz 3'te." : "Zu Informationszwecken; offizieller UStVA/BMD-Export folgt in Phase 3."}
      </p>
    </div>
  );
}

/* ===== P&L ===== */
function PnLTab() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { (async () => setData((await api.get("/finance/pnl?months=12")).data))(); }, []);
  if (!data) return <div className="grid grid-cols-3 gap-3">{[0,1,2].map((i) => <SkeletonKPICard key={i} />)}</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label={t("revenue")} value={money(data.total_revenue)} />
        <Stat label={t("expenses")} value={money(data.total_expenses)} />
        <Stat label={t("profitLoss")} value={money(data.total_profit)} accent />
      </div>
      <div className="border rounded-xl overflow-hidden shadow-elev-1 bg-background">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b">
            <tr>
              <th className="text-left px-4 py-3 h-11 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{lang === "tr" ? "Ay" : "Monat"}</th>
              <th className="text-right px-4 py-3 h-11 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{t("revenue")}</th>
              <th className="text-right px-4 py-3 h-11 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{t("expenses")}</th>
              <th className="text-right px-4 py-3 h-11 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">{t("outputVat")}</th>
              <th className="text-right px-4 py-3 h-11 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{t("profitLoss")}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.months.map((m) => (
              <tr key={m.month} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 font-mono">{m.month}</td>
                <td className="text-right px-4 py-3 font-mono">{money(m.revenue)}</td>
                <td className="text-right px-4 py-3 font-mono">{money(m.expenses)}</td>
                <td className="text-right px-4 py-3 font-mono text-muted-foreground hidden md:table-cell">{money(m.vat_collected)}</td>
                <td className={`text-right px-4 py-3 font-mono font-semibold ${m.profit >= 0 ? "text-emerald-600" : "text-destructive"}`}>{money(m.profit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== Bank Accounts ===== */
function BankAccountsTab() {
  const { t, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const empty = {
    name: "", bank_name: "", iban: "", currency: "EUR",
    balance: 0, opening_balance: 0, is_cash: false,
    notes: "", active: true,
  };
  const [form, setForm] = useState(empty);
  const [correctionReason, setCorrectionReason] = useState("");
  const [errors, setErrors] = useState({});
  const [adjustments, setAdjustments] = useState([]);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/bank-accounts")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = lang === "tr" ? "Ad zorunlu" : "Name erforderlich";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const openCreate = () => {
    setEditingId(null); setForm(empty); setCorrectionReason("");
    setAdjustments([]); setErrors({}); setOpen(true);
  };
  const openEdit = async (row) => {
    setEditingId(row.id);
    setErrors({});
    setCorrectionReason("");
    setForm({
      name: row.name || "",
      bank_name: row.bank_name || "",
      iban: row.iban || "",
      currency: row.currency || "EUR",
      balance: row.balance ?? 0,
      opening_balance: row.opening_balance ?? row.balance ?? 0,
      is_cash: !!row.is_cash,
      notes: row.notes || "",
      active: row.active !== false,
    });
    try {
      const { data } = await api.get(`/bank-accounts/${row.id}/adjustments`);
      setAdjustments(data || []);
    } catch { setAdjustments([]); }
    setOpen(true);
  };
  const submit = async () => {
    if (!validate()) return;
    const balanceChanged = editingId && rows.find(r => r.id === editingId)?.balance !== +form.balance;
    const payload = {
      ...form,
      balance: +form.balance,
      opening_balance: +form.opening_balance,
      meta: balanceChanged && correctionReason ? { correction_reason: correctionReason } : undefined,
    };
    try {
      if (editingId) {
        await api.put(`/bank-accounts/${editingId}`, payload);
        toast.success(lang === "tr" ? "Hesap güncellendi" : "Konto aktualisiert");
      } else {
        await api.post("/bank-accounts", payload);
        toast.success(t("save"));
      }
      setOpen(false); setForm(empty); setEditingId(null); setCorrectionReason("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/bank-accounts/${id}`); await load(); toast.success(t("delete")); };
  const totalBalance = useMemo(() => rows.reduce((s, r) => s + (r.balance || 0), 0), [rows]);
  const balanceChanged = editingId && rows.find(r => r.id === editingId)?.balance !== +form.balance;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Stat label={t("cashOnHand")} value={money(totalBalance)} accent icon={Landmark} />
        <Button onClick={openCreate} data-testid="new-account-btn"><Plus className="w-4 h-4 mr-2" /> {t("newAccount")}</Button>
      </div>
      {loading ? (
        <div className="grid gap-3">{[0,1].map((i) => <SkeletonKPICard key={i} />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Landmark} title={lang === "tr" ? "Hesap yok" : "Keine Konten"} description={lang === "tr" ? "İlk banka hesabını ekle." : "Erstes Bankkonto anlegen."} action={{ label: t("newAccount"), icon: Plus, onClick: openCreate }} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((a) => (
            <Card
              key={a.id}
              onClick={() => openEdit(a)}
              className="border rounded-xl shadow-elev-1 card-hover overflow-hidden relative cursor-pointer"
              data-testid={`bank-card-${a.id}`}
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/70 to-primary/40" />
              <CardContent className="p-5 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Landmark className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-display font-semibold tracking-tight">{a.name}</div>
                      <div className="flex gap-1 mt-0.5">
                        {a.is_cash && <Badge variant="secondary" className="text-[10px] rounded-md">{t("cash")}</Badge>}
                        {a.bank_name && <Badge variant="outline" className="text-[10px] rounded-md">{a.bank_name}</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono mt-3 truncate">{a.iban || "—"}</div>
                  <div className="font-mono text-3xl font-bold tracking-[-0.02em] mt-2">{money(a.balance)}</div>
                </div>
                <div
                  className="flex flex-col gap-1 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => openEdit(a)}
                    className="h-8 w-8 rounded-lg hover:bg-primary/10"
                    title={lang === "tr" ? "Düzenle" : "Bearbeiten"}
                    data-testid={`edit-bank-${a.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <ConfirmDeleteButton onConfirm={() => remove(a.id)} itemName={a.name || a.iban} data-testid={`del-bank-${a.id}`} className="h-8 w-8 rounded-lg hover:bg-destructive/10" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="!max-w-md !w-full
                     !left-0 !top-auto !bottom-0 !right-auto !translate-x-0 !translate-y-0
                     !h-[100dvh] !max-h-[100dvh]
                     sm:!left-[50%] sm:!top-[50%] sm:!bottom-auto sm:!right-auto
                     sm:!translate-x-[-50%] sm:!translate-y-[-50%]
                     sm:!h-auto sm:!max-h-[92vh] sm:!w-[calc(100vw-2rem)]
                     !p-0 !gap-0 flex flex-col rounded-none sm:rounded-lg overflow-hidden"
          data-testid="bank-dialog"
        >
          <DialogHeader className="flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4 border-b bg-background">
            <DialogTitle data-testid="bank-dialog-title">
              {editingId
                ? (lang === "tr" ? "Hesabı Düzenle" : "Konto bearbeiten")
                : t("newAccount")}
            </DialogTitle>
          </DialogHeader>
          <div
            className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 space-y-3"
            style={{ WebkitOverflowScrolling: "touch" }}
            data-testid="bank-dialog-body"
          >
            <div>
              <Label>{t("accountName")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bank-name" className="mt-1" />
              {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label>{lang === "tr" ? "Banka" : "Bankname"}</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-bankname" className="mt-1" placeholder={lang === "tr" ? "örn. Erste, Sparkasse" : "z.B. Erste, Sparkasse"} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("iban")}</Label>
                <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="mt-1 font-mono" data-testid="bank-iban" />
              </div>
              <div>
                <Label>{lang === "tr" ? "Para birimi" : "Währung"}</Label>
                <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="mt-1 font-mono uppercase" maxLength={3} data-testid="bank-currency" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{lang === "tr" ? "Açılış bakiye" : "Anfangssaldo"}</Label>
                <Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="bank-opening-balance" className="mt-1 font-mono" />
              </div>
              <div>
                <Label>{lang === "tr" ? "Mevcut bakiye" : "Aktueller Saldo"} (€)</Label>
                <Input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="bank-balance" className="mt-1 font-mono" />
              </div>
            </div>
            {editingId && balanceChanged && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 space-y-2" data-testid="bank-correction-hint">
                <div className="text-xs text-amber-900 font-semibold">
                  {lang === "tr"
                    ? "Bakiye düzeltmesi denetim kaydına yazılacak."
                    : "Saldo-Korrektur wird im Audit-Log festgehalten."}
                </div>
                <Input
                  value={correctionReason}
                  onChange={(e) => setCorrectionReason(e.target.value)}
                  placeholder={lang === "tr" ? "Düzeltme nedeni (isteğe bağlı)" : "Grund (optional)"}
                  data-testid="bank-correction-reason"
                  className="bg-background/60 text-sm"
                />
              </div>
            )}
            <div>
              <Label>{lang === "tr" ? "Notlar" : "Notizen"}</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="bank-notes" className="mt-1" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_cash} onChange={(e) => setForm({ ...form, is_cash: e.target.checked })} data-testid="bank-is-cash" />
              {t("isCash")}
            </label>
            {editingId && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="bank-active" />
                {lang === "tr" ? "Aktif" : "Aktiv"}
              </label>
            )}
            {editingId && adjustments.length > 0 && (
              <div className="mt-4 border rounded-lg" data-testid="bank-adjustments">
                <div className="px-3 py-2 border-b bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                  {lang === "tr" ? "Bakiye Düzeltme Geçmişi" : "Saldo-Korrektur Historie"}
                </div>
                <ul className="divide-y">
                  {adjustments.slice(0, 5).map((a) => (
                    <li key={a.id} className="px-3 py-2 text-xs flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono">{formatDateAT(a.created_at)}</div>
                        {a.reason && <div className="text-muted-foreground truncate">{a.reason}</div>}
                      </div>
                      <div className={`font-mono font-semibold ${a.delta >= 0 ? "text-emerald-700" : "text-destructive"}`}>
                        {a.delta >= 0 ? "+" : ""}{money(a.delta)}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div
            className="flex-shrink-0 flex justify-end gap-2 px-4 py-3 sm:px-6 border-t bg-background/95 backdrop-blur
                       [padding-bottom:calc(env(safe-area-inset-bottom)+0.75rem)]"
            data-testid="bank-dialog-footer"
          >
            <Button variant="outline" onClick={() => setOpen(false)} className="min-h-[44px]" data-testid="cancel-bank">
              {t("cancel")}
            </Button>
            <Button onClick={submit} className="min-h-[44px]" data-testid="save-bank">
              {editingId
                ? (lang === "tr" ? "Değişiklikleri Kaydet" : "Änderungen speichern")
                : t("save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===== Suppliers ===== */
function SuppliersTab() {
  const { t, lang } = useI18n();
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const empty = { name: "", contact_person: "", email: "", phone: "", tax_number: "", default_payment_terms_days: 30, notes: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = lang === "tr" ? "Ad zorunlu" : "Name erforderlich";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const submit = async () => {
    if (!validate()) return;
    try {
      await api.post("/suppliers", { ...form, default_payment_terms_days: parseInt(form.default_payment_terms_days) || 30 });
      setOpen(false); setForm(empty); setErrors({}); setRefreshKey((k) => k + 1);
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/suppliers/${id}`); setRefreshKey((k) => k + 1); toast.success(t("delete")); };

  const columns = [
    { key: "name", label: t("supplierName"), sortable: true, className: "font-medium", render: (r) => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
          {(r.name || "?").slice(0, 1).toUpperCase()}
        </div>
        <span className="truncate">{r.name}</span>
      </div>
    ) },
    { key: "contact_person", label: t("contactPerson"), render: (r) => r.contact_person || <span className="text-muted-foreground">—</span> },
    { key: "email", label: t("email"), className: "font-mono text-xs text-muted-foreground" },
    { key: "phone", label: t("phone"), className: "font-mono text-xs" },
    { key: "default_payment_terms_days", label: t("paymentTerms"), className: "text-right font-mono", render: (r) => (
      <Badge variant="secondary" className="rounded-md text-[10px]">{r.default_payment_terms_days} {lang === "tr" ? "gün" : "T"}</Badge>
    ) },
    { key: "actions", label: "", className: "text-right w-10", render: (r) => (
      <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`del-sup-${r.id}`} className="h-8 w-8 rounded-lg hover:bg-destructive/10"><Trash2 className="w-4 h-4 text-destructive" /></Button>
    )},
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="new-supplier-btn"><Plus className="w-4 h-4 mr-2" /> {t("newSupplier")}</Button>
      </div>
      <DataTable
        columns={columns}
        fetcher={async (p) => {
          const params = new URLSearchParams({ page: p.page, page_size: p.page_size, sort: p.sort || "name", order: p.order || "asc" });
          if (p.search) params.set("search", p.search);
          const { data } = await api.get(`/suppliers?${params.toString()}`);
          return data;
        }}
        defaultSort="name"
        defaultOrder="asc"
        pageSize={10}
        refreshKey={refreshKey}
        rowTestId="sup-row"
        searchPlaceholder={lang === "tr" ? "Tedarikçi ara…" : "Suchen…"}
        emptyProps={{ icon: Landmark, title: lang === "tr" ? "Tedarikçi yok" : "Keine Lieferanten", description: lang === "tr" ? "Faturaları ilişkilendirmek için tedarikçi ekle." : "Erstes anlegen, um Rechnungen zu verknüpfen.", action: { label: t("newSupplier"), icon: Plus, onClick: () => setOpen(true) } }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("newSupplier")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("supplierName")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sup-name" className="mt-1" />{errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("contactPerson")}</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 font-mono" /></div>
              <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>{lang === "tr" ? "Vergi No" : "UID"}</Label><Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} className="mt-1 font-mono" /></div>
              <div><Label>{t("paymentTerms")}</Label><Input type="number" min="0" value={form.default_payment_terms_days} onChange={(e) => setForm({ ...form, default_payment_terms_days: e.target.value })} className="mt-1 font-mono" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-sup">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===== Shared Stat ===== */
function Stat({ label, value, accent, icon: Icon }) {
  return (
    <div className="border rounded-xl p-5 bg-background shadow-elev-1 card-hover">
      <div className="flex items-center justify-between text-muted-foreground mb-2.5">
        <span className="text-[10px] uppercase tracking-[0.14em] font-semibold">{label}</span>
        {Icon && <div className="w-7 h-7 rounded-lg bg-muted/60 flex items-center justify-center"><Icon className="w-3.5 h-3.5" /></div>}
      </div>
      <div className={`font-mono text-xl md:text-2xl font-semibold tracking-[-0.02em] ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
